// src/components/SignupForm.jsx
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  createUserWithEmailAndPassword,
  sendEmailVerification,
} from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { auth, db } from "../firebase";
import { setDoc, doc } from "firebase/firestore";
import { useAlert } from "../components/CustomAlert";

function SignupForm({ showConfirmPassword, switchLanguage }) {
  const { t: tSignup } = useTranslation("signup");
  const { t: tAuth } = useTranslation("auth");
  const { t: tVerify } = useTranslation("verify");
  const { t: tActions } = useTranslation("actions");
  const { t: tCommon } = useTranslation("common");
  const { i18n } = useTranslation();

  const navigate = useNavigate();
  const { showAlert } = useAlert();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSignup = async function (e) {
    e.preventDefault();

    if (isSubmitting) return;
    setIsSubmitting(true);

    var normalizedEmail = email ? email.trim().toLowerCase() : "";

    // ✅ 邮箱校验
    if (!normalizedEmail) {
      showAlert(
        tAuth("signupFailed"),
        tCommon("errorInvalidEmail"),
        tActions("ok") || "我知道了",
        "error"
      );
      setIsSubmitting(false);
      return;
    }

    // ✅ 密码校验
    if (!password || password.length < 6) {
      showAlert(
        tAuth("signupFailed"),
        tCommon("errorWeakPassword"),
        tActions("ok") || "我知道了",
        "error"
      );
      setIsSubmitting(false);
      return;
    }

    // ✅ 确认密码校验
    if (showConfirmPassword && password !== confirmPassword) {
      showAlert(
        tAuth("signupFailed"),
        tSignup("passwordMismatch"),
        tActions("ok") || "我知道了",
        "error"
      );
      setIsSubmitting(false);
      return;
    }

    try {
      // 1️⃣ 创建账号
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        normalizedEmail,
        password
      );
      const user = userCredential.user;

      // 2️⃣ 写入 Firestore（非阻塞）
      try {
        await setDoc(doc(db, "users", user.uid), {
          uid: user.uid,
          email: user.email,
          createdAt: new Date(),
        });
      } catch (fireErr) {
        console.error("Firestore setDoc error (non-blocking):", fireErr);
      }

      // 3️⃣ 发送验证邮件（固定使用 riceroma.ca + 规范化语言 zh / en）
      var base = "https://riceroma.ca";
      var currentLang = "zh";
      if (i18n && i18n.language === "en") {
        currentLang = "en";
      }

      // 记一份统一的语言 key，后面 VerifyEmail / AuthPage 都用这个
      try {
        localStorage.setItem("selectedLang", currentLang);
      } catch (eStore) {
        console.warn("save selectedLang failed:", eStore);
      }

      // ⚠️ 这里改成 ui= 彻底绕开 Firebase 自己的 lang 参数
      const actionCodeSettings = {
        url: base + "/verify-email?ui=" + currentLang,
        handleCodeInApp: true,
      };

      try {
        await sendEmailVerification(user, actionCodeSettings);

        // 4️⃣ 保存登录信息
        localStorage.setItem("riceroma_email", normalizedEmail);
        localStorage.setItem("riceroma_password", password);

        // 5️⃣ 成功提示
        showAlert(
          tAuth("signupSuccess") || "注册成功",
          tAuth("verificationSent") || "验证邮件已发送，请查收",
          tActions("ok") || "我知道了",
          "success",
          async function () {
            try {
              await auth.signOut();
            } catch (signoutErr) {
              console.warn("signOut after signup warning:", signoutErr);
            }
            navigate("/login");
          }
        );
      } catch (mailError) {
        console.error("sendEmailVerification error:", mailError);
        const mailMsg = tVerify("error") || "验证邮件发送失败";
        showAlert(
          tAuth("signupFailed"),
          mailMsg + "：" + (mailError.message || ""),
          tActions("ok") || "我知道了",
          "error"
        );
      }
    } catch (err) {
      console.error("Signup error:", err);
      var errorMessageKey = "errorDefault";
      if (err.code === "auth/email-already-in-use") {
        errorMessageKey = "errorEmailInUse";
      } else if (err.code === "auth/invalid-email") {
        errorMessageKey = "errorInvalidEmail";
      } else if (err.code === "auth/weak-password") {
        errorMessageKey = "errorWeakPassword";
      }

      showAlert(
        tAuth("signupFailed"),
        tCommon(errorMessageKey),
        tActions("ok") || "我知道了",
        "error"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLangToggle = function () {
    if (!switchLanguage) return;
    var newLang = i18n.language === "zh" ? "en" : "zh";
    switchLanguage(newLang);
    try {
      localStorage.setItem("selectedLang", newLang);
    } catch (eStore) {
      console.warn("save selectedLang failed:", eStore);
    }
  };

  return (
    <form onSubmit={handleSignup} className="auth-form" noValidate>
      <label>
        {tSignup("email")}
        <input
          type="email"
          inputMode="email"
          autoComplete="email"
          name="email"
          value={email}
          onChange={function (e) {
            setEmail(e.target.value);
          }}
          required
          placeholder={tSignup("emailPlaceholder")}
          aria-label={tSignup("email")}
        />
      </label>

      <label>
        {tSignup("password")}
        <input
          type="password"
          autoComplete="new-password"
          name="password"
          value={password}
          onChange={function (e) {
            setPassword(e.target.value);
          }}
          required
          placeholder={tSignup("passwordPlaceholder")}
          aria-label={tSignup("password")}
          minLength={6}
        />
      </label>

      {showConfirmPassword && (
        <label>
          {tSignup("confirmPassword")}
          <input
            type="password"
            autoComplete="new-password"
            name="confirmPassword"
            value={confirmPassword}
            onChange={function (e) {
              setConfirmPassword(e.target.value);
            }}
            required
            placeholder={tSignup("confirmPasswordPlaceholder")}
            aria-label={tSignup("confirmPassword")}
            minLength={6}
          />
        </label>
      )}

      <div className="login-actions">
        <button
          type="submit"
          className="action-button active"
          disabled={isSubmitting}
          aria-busy={isSubmitting}
        >
          {isSubmitting ? tSignup("button") + "…" : tSignup("button")}
        </button>

        <button
          type="button"
          className="action-button inactive"
          onClick={handleLangToggle}
        >
          {i18n.language === "zh"
            ? tSignup("english") || "English"
            : tSignup("chinese") || "中文"}
        </button>
      </div>
    </form>
  );
}

export default SignupForm;
